import React, { useState } from 'react';
import moment from 'moment';
import Image from 'next/image';
import { auth } from '../firebase/firebaseApp';
import { RichText } from '@graphcms/rich-text-react-renderer';
import Share from './Share';
import Link from 'next/link';
import { Plus, Square } from 'lucide-react'; // Optional: Use Lucide icons if available, or text fallbacks

const PostDetail = ({ post }) => {
  const user = auth.currentUser;
  const [showAllCategories, setShowAllCategories] = useState(false);
  const categories = post.categories || [];
  const displayedCategories = showAllCategories ? categories : categories.slice(0, 3);

  return (
    <>
      {/* Title, description, canonical, social and structured data are set once, in pages/category/blog/[slug].js */}

      {/* Main Container: Rigid Box, No Radius */}
      <div className="bg-white border-2 border-black border-b-4 w-full max-w-7xl mx-auto mb-12">

        {/* --- 1. TECH HEADER BAR (Date & Status) --- */}
        <div className="flex justify-between items-center border-b-2 border-black p-3 bg-zinc-50">
          <div className="font-mono text-xs font-bold tracking-widest uppercase text-black">
            {moment(post.createdAt).format('YYYY.MM.DD')}
          </div>
        </div>

        {/* --- 2. TITLE BLOCK (Brutalist) --- */}
        <div className="border-b-2 border-black p-6 md:p-10">
          <h1 className="text-left text-black text-4xl md:text-6xl font-black uppercase leading-[0.9] tracking-tighter">
            {post.title}
          </h1>
        </div>

        {/* --- 3. META GRID (Author & Categories) --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 border-b-2 border-black">

          {/* Author Section: Left Column */}
          <div className="flex items-center p-6 border-b-2 md:border-b-0 md:border-r-2 border-black">
            <div className="relative h-14 w-14 mr-4 border">
              {/* Hard Square Image */}
              <div className="relative w-full h-full bg-zinc-200">
                <Image
                  unoptimized
                  alt={post.username}
                  fill
                  quality={10}
                  className="object-cover transition-all duration-300"
                  src={post.profileURL !== "" ? post.profileURL : "/assets/Juliano pfp.png"}
                  sizes="100px"
                />
              </div>
            </div>
            <div className="flex flex-col justify-center">
              <span className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-1">Author</span>
              <div className="text-black font-bold uppercase text-sm leading-tight">
                {post.author.name}
              </div>
              <span className="text-xs text-zinc-600 font-mono mt-1 max-w-[200px] truncate">
                {post.author.bio}
              </span>
            </div>
          </div>

          {/* Categories Section: Right Column */}
          <div className="flex items-center justify-start md:justify-end p-6 bg-zinc-50">
            <div className="flex flex-wrap gap-2 items-center">
              {displayedCategories.map((cat, idx) => (
                <Link
                  key={idx}
                  href={`/category/${cat.slug}`}
                  className="font-mono text-[10px] uppercase tracking-wider text-black border border-black px-2 py-1 bg-white hover:bg-[#FF3333] hover:text-white transition-colors duration-0"
                >
                  {cat.name}
                </Link>
              ))}

              {categories.length > 3 && !showAllCategories && (
                <button
                  className="flex items-center justify-center w-6 h-6 border border-black bg-white hover:bg-black hover:text-white transition-colors"
                  onClick={(e) => { e.preventDefault(); setShowAllCategories(true); }}
                  aria-label="Show all categories"
                >
                  <span className="font-mono text-xs">+</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* --- 4. FEATURED IMAGE (Full Bleed) --- */}
        <div className="w-full border-b-2 border-black relative bg-zinc-100">
          {/* Decorative accent line */}
          <div className="absolute top-0 left-0 w-2 h-full bg-[#0044FF] z-10 hidden md:block"></div>
          <Image
            src={post.featuredImage[0].url}
            alt={post.altImage}
            width={1200}
            height={800}
            quality={75}
            style={{ width: "100%", height: "auto" }}
            className="block transition-all duration-700 ease-in-out"
            priority
          />
        </div>

        {/* --- 5. ARTICLE CONTENT --- */}
        <div className="flex flex-col items-center justify-center py-16 md:py-24 w-full px-6">
          <div className="max-w-[700px] w-full">
            <RichText
              content={JSON.parse(post.postText)}
              renderers={{
                // H1 mapped to H2 style inside article to separate from Main Title
                h1: ({ children }) => (
                  <h2 className="text-black font-black uppercase text-3xl md:text-4xl mt-12 mb-6 border-l-4 border-[#FF3333] pl-4 leading-none">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-xl md:text-2xl text-black font-bold uppercase tracking-tight mt-16 mb-6">
                    {children}
                  </h3>
                ),
                h4: ({ children }) => (
                  <h4 className="text-lg text-black font-mono font-bold uppercase border-b border-black pb-2 mt-12 mb-6">
                    {children}
                  </h4>
                ),
                p: ({ children }) => (
                  <p className="mb-6 text-black text-lg leading-relaxed font-sans selection:bg-[#FFCC00]">
                    {children}
                  </p>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal list-inside mb-8 pl-4 space-y-2 text-black text-lg leading-relaxed border-l border-zinc-300">
                    {children}
                  </ol>
                ),
                ul: ({ children }) => (
                  <ul className="list-square list-inside mb-8 pl-4 space-y-2 text-black text-lg leading-relaxed">
                    {children}
                  </ul>
                ),
                table: ({ children }) => (
                  <div className="my-10 overflow-x-auto border-2 border-black">
                    <table className="w-full border-collapse text-left text-black text-sm md:text-base">{children}</table>
                  </div>
                ),
                table_head: ({ children }) => <thead className="bg-zinc-100 border-b-2 border-black">{children}</thead>,
                table_body: ({ children }) => <tbody>{children}</tbody>,
                table_row: ({ children }) => <tr className="border-b border-zinc-300 last:border-b-0">{children}</tr>,
                table_header_cell: ({ children }) => (
                  <th className="px-4 py-3 align-bottom font-mono text-xs font-bold uppercase tracking-wider">{children}</th>
                ),
                table_cell: ({ children }) => <td className="px-4 py-3 align-top leading-snug">{children}</td>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-black pl-6 py-2 my-10 bg-zinc-50 text-xl italic font-serif text-zinc-800">
                    "{children}"
                  </blockquote>
                ),
                bold: ({ children }) => (
                  <strong className="font-black text-black">{children}</strong>
                ),
                a: ({ href, children }) => {
                  return (
                    <a target="_blank" rel="noopener noreferrer" className="text-[#0044FF] font-bold hover:bg-[#0044FF] hover:text-white transition-colors px-1" href={href}>
                      {children}
                    </a>
                  )
                },
                img: ({ src, width, height, title }) => (
                  <div className="my-12 border border-black p-2 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                    <Image unoptimized width={width} height={height} className="w-full h-auto grayscale hover:grayscale-0 transition-all" src={src} alt={title} />
                    {title && <p className="font-mono text-[10px] uppercase mt-2 text-zinc-500">{title}</p>}
                  </div>
                ),
                code: ({ children }) => (
                  <div className="bg-black text-zinc-300 font-mono text-xs p-4 overflow-x-auto border-l-4 border-[#FFCC00] my-8 whitespace-pre-wrap">
                    <div dangerouslySetInnerHTML={{ __html: children }} />
                  </div>
                )
              }}
            />
          </div>

          {/* --- 6. FOOTER / SHARE --- */}
          <div className="w-full max-w-[700px] mt-20 pt-10 border-t-2 border-black">
            <div className="font-mono text-xs uppercase mb-4 text-zinc-500">:: Share</div>
            <Share isBlog={true} url={"monet.io/category/blog/"} post={post} uid={user?.uid} />
          </div>

        </div>
      </div>
    </>
  );
};

export default PostDetail;